import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Video, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Avatar } from "../saker/Avatar";
import { datoOm } from "../saker/sakerUtils";

const chipKl = (aktiv) =>
  `inline-flex items-center gap-1.5 text-[12px] font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
    aktiv ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] bg-white text-neutral-600 hover:border-neutral-400"
  }`;

const Seksjon = ({ label, children }) => (
  <div className="mt-4">
    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">{label}</p>
    {children}
  </div>
);

const EPOST_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const nesteHalvtime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export default function NyttMoteModal({ members, organizer, forvalg, onLagret, onLukk }) {
  const [form, setForm] = useState({
    subject: forvalg?.subject || "",
    date: datoOm(0),
    startTime: nesteHalvtime(),
    durationMin: 50,
    body: "",
    teams: true,
  });
  const [valgte, setValgte] = useState(() => (forvalg?.attendees || []).filter((e) => EPOST_RE.test(e)));
  const [friEpost, setFriEpost] = useState("");
  const [lagrer, setLagrer] = useState(false);
  const tittelRef = useRef(null);

  useEffect(() => { tittelRef.current?.focus(); }, []);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); onLukk(); } };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [onLukk]);

  const sett = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const medEpost = members.filter((m) => (m.email || "").trim());

  const toggleDeltaker = (email) => {
    const e = email.trim().toLowerCase();
    setValgte((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));
  };

  const leggTilFri = () => {
    const e = friEpost.trim().toLowerCase();
    if (!e) return;
    if (!EPOST_RE.test(e)) { toast.error("Ugyldig e-postadresse"); return; }
    if (!valgte.includes(e)) setValgte((cur) => [...cur, e]);
    setFriEpost("");
  };

  const lagre = async () => {
    if (!form.subject.trim() || lagrer) return;
    setLagrer(true);
    try {
      const { data } = await api.post("/admin/meetings", {
        ...form,
        subject: form.subject.trim(),
        attendees: valgte,
        taskId: forvalg?.taskId || null,
      });
      if (data.warning) {
        toast.warning(data.warning, { duration: 8000 });
      } else {
        toast.success(
          data.meeting?.teams && data.meeting?.joinUrl
            ? "Teams-møte opprettet — invitasjoner sendt fra Outlook"
            : "Møte opprettet — invitasjoner sendt fra Outlook"
        );
      }
      onLagret(data.meeting);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke opprette møtet");
    } finally {
      setLagrer(false);
    }
  };

  const friMedlemsEposter = new Set(medEpost.map((m) => m.email.trim().toLowerCase()));
  const eksterne = valgte.filter((e) => !friMedlemsEposter.has(e));

  return (
    <div className="fixed inset-0 z-[65] flex items-start justify-center pt-[6vh] sm:pt-[10vh] px-4" data-testid="nytt-mote-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/30" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[600px] max-h-[86vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-[#E7E4DB]">
        <div className="flex items-center justify-between px-5 pt-4">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Nytt møte {organizer && <span className="normal-case tracking-normal">· arrangør {organizer}</span>}</span>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="nytt-mote-lukk">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 pt-2 pb-5">
          <input
            ref={tittelRef}
            value={form.subject}
            onChange={(e) => sett("subject", e.target.value)}
            maxLength={300}
            placeholder="Hva handler møtet om?"
            className="w-full bg-transparent text-[17px] font-semibold text-neutral-900 focus:outline-none placeholder:text-neutral-300 py-1"
            data-testid="nytt-mote-tittel"
          />

          <Seksjon label="Når">
            <div className="flex flex-wrap items-center gap-1.5">
              {[{ l: "I dag", d: 0 }, { l: "I morgen", d: 1 }].map((h) => (
                <button key={h.l} type="button" onClick={() => sett("date", datoOm(h.d))} className={chipKl(form.date === datoOm(h.d))} data-testid={`nytt-mote-dato-${h.d}`}>
                  {h.l}
                </button>
              ))}
              <input
                type="date"
                value={form.date}
                onChange={(e) => sett("date", e.target.value)}
                className="text-[12px] border border-[#E7E4DB] rounded-lg px-2.5 py-[5px] bg-white text-neutral-600 focus:outline-none focus:border-neutral-400 transition-colors"
                data-testid="nytt-mote-dato"
              />
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => sett("startTime", e.target.value)}
                className="text-[12px] border border-[#E7E4DB] rounded-lg px-2.5 py-[5px] bg-white text-neutral-600 focus:outline-none focus:border-neutral-400 transition-colors"
                data-testid="nytt-mote-tid"
              />
            </div>
          </Seksjon>

          <Seksjon label="Varighet">
            <div className="flex flex-wrap gap-1.5" data-testid="nytt-mote-varighet">
              {[25, 50, 90, 120].map((min) => (
                <button key={min} type="button" onClick={() => sett("durationMin", min)} className={chipKl(form.durationMin === min)} data-testid={`nytt-mote-varighet-${min}`}>
                  {min} min
                </button>
              ))}
            </div>
          </Seksjon>

          <Seksjon label="Deltakere">
            <div className="flex flex-wrap gap-1.5">
              {medEpost.map((m) => {
                const e = m.email.trim().toLowerCase();
                return (
                  <button key={m.id} type="button" onClick={() => toggleDeltaker(e)} className={chipKl(valgte.includes(e))} data-testid={`nytt-mote-deltaker-${m.id}`}>
                    <Avatar person={m} size={17} />
                    {m.name.split(" ")[0]}
                  </button>
                );
              })}
              {eksterne.map((e) => (
                <span key={e} className="inline-flex items-center gap-1.5 text-[12px] font-medium rounded-lg px-2.5 py-1.5 border border-neutral-900 bg-neutral-900 text-white">
                  {e}
                  <button type="button" onClick={() => toggleDeltaker(e)} className="text-white/60 hover:text-white transition-colors">
                    <X size={11} />
                  </button>
                </span>
              ))}
              <input
                value={friEpost}
                onChange={(e) => setFriEpost(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); leggTilFri(); } }}
                onBlur={leggTilFri}
                placeholder="+ ekstern e-post"
                className="text-[12px] text-neutral-600 bg-transparent border border-dashed border-[#E7E4DB] rounded-lg px-2.5 py-1.5 w-[140px] focus:w-[200px] focus:outline-none focus:border-neutral-400 transition-all placeholder:text-neutral-400"
                data-testid="nytt-mote-epost-input"
              />
            </div>
          </Seksjon>

          <Seksjon label="Agenda (valgfritt)">
            <textarea
              value={form.body}
              onChange={(e) => sett("body", e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="Kort agenda — vises i invitasjonen"
              className="w-full text-[13px] leading-relaxed text-neutral-700 border border-[#E7E4DB] rounded-lg px-3 py-2 focus:outline-none focus:border-neutral-400 transition-colors resize-none placeholder:text-neutral-300"
              data-testid="nytt-mote-agenda"
            />
          </Seksjon>

          <button
            type="button"
            onClick={() => sett("teams", !form.teams)}
            className="mt-4 w-full flex items-center justify-between py-1 text-left"
            data-testid="nytt-mote-teams-toggle"
          >
            <span className="flex items-center gap-2">
              <Video size={14} className={form.teams ? "text-indigo-600" : "text-neutral-300"} />
              <span>
                <span className="block text-[13px] font-medium text-neutral-800">Teams-møte</span>
                <span className="block text-[11px] text-neutral-400">Legger til Teams-lenke i invitasjonen</span>
              </span>
            </span>
            <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${form.teams ? "bg-neutral-900" : "bg-neutral-300"}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form.teams ? "left-[18px]" : "left-0.5"}`} />
            </span>
          </button>

          <div className="flex items-center justify-end gap-2 mt-5">
            <button type="button" onClick={onLukk} className="text-[12.5px] text-neutral-500 px-3 py-2 hover:text-neutral-800 transition-colors">
              Avbryt
            </button>
            <button
              type="button"
              onClick={lagre}
              disabled={!form.subject.trim() || lagrer}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-neutral-900 text-white px-4 py-2 rounded-lg hover:bg-neutral-700 disabled:opacity-30 transition-all"
              data-testid="nytt-mote-lagre"
            >
              {lagrer ? <><Loader2 size={13} className="animate-spin" /> Oppretter …</> : "Opprett og send invitasjoner"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
